import { config } from '@abyss/web/tools/config';
import { storage } from '@abyss/web/tools/storage';
import get from 'lodash/get';
import { useCallback, useContext, useEffect, useState } from 'react';
import { useShallow } from 'zustand/react/shallow';

import { getMemberRelvancySpecialityRollupCodes } from '../../../../api/src/utils/specialityUtils/specialityUtils';
import {
  CareCategories,
  Constants,
  CoverageTypesCodes,
  OPTUM_CORRELATION_ID_HEADER,
  ProviderTypes,
  ReverseCoverageTypesCodes,
} from '../../common/Constants';
import { ConstantsLagoon } from '../../common/ConstantsLagoon';
import { PortalContext } from '../../context/PortalContext';
import { isMissingParameters } from '../../errors/helpers';
import { getLanguage } from '../../frontends/ProviderSearch/context/Internationalization/helpers';
import { useCurrentMemberIsPreEffective } from '../../hooks/useCurrentMemberIsPreEffective';
import { CustomPreferredPolicy } from '../../models/Lagoon';
import { Address, RecommendationDetails } from '../../models/ProviderDetails';
import { useChipStore } from '../../store/useChipStore';
import { ChipState } from '../../store/useChipStore/chipStore';
import { conditionalValueRender } from '../../utils/common';
import { returnSuppressFlag } from '../../utils/featureSuppress';
import { shouldSuppressVirtualOnlyLocationIndicator } from '../../utils/indicator.utils';
import {
  getAutoIncrementRadius,
  getExcludeNatAncForNetworkIds,
  getLagoonConfigValue,
  getLob,
  getMemberHealthCoverageType,
  getProviderIds,
  getRecommendationPayload,
  getRegionalVirtualCareProviderArray,
  getSearchRadius,
  getSortByValue,
  getVirtualCareLicenseStateCodes,
  shouldBlockNationalProviders,
  shouldEnableRegionalVirtualCareSection,
} from '../../utils/providerSearch.utils';
import {
  getClaimOrEligibilitySystemTypeCode,
  getCoverageEffDateRange,
  getCoverageTypes,
  getCurrentMember,
  getIsCustomPreferredPolicy,
  getIsPolicyIDRequired,
  getMemberPlanYear,
  getNetworkIdsForPES,
  getOnlineRetailers,
  getPlanVariationCode,
  getRulesPackageKey,
  getSitusState,
  isPlanVariationCodeRequired,
  memberHasBHCoverage,
  useIsEligibleForStandaloneDental,
} from '../../utils/user.utils';
import { useCustomQuery } from '../useCustomQuery';
import { useFeatureFlag } from '../useFeatureFlag';
import { useGeoLocationStorage } from '../useGeoLocationStorage';
import { useLagoon } from '../useLagoon';
import { useMinProviderCount } from '../useMinProviderCount';
import { useSmartChoice } from '../useSmartChoice';
import GET_SNACK_CARD_PROVIDER_SEARCH_QUERY from './SnackCardProviderSearch.graphql';

export interface SnackCardProvider {
  providerId: string;
  providerName: string;
  providerType: string;
  primaryDegrees?: string[];
  specialities?: string[];
  distance: string;
  locationId: string;
  networkStatus?: string;
  npi: string;
  healthGradeRating?: string;
  organizationType?: string[];
  organizationCode?: string[];
  speciality: string;
  imageLocation: string;
  isTieredProvider?: boolean;
  isTier2Provider?: boolean;
  isOxPreferredLab?: boolean;
  isPlatinumProvider?: boolean;
  isPreferredLab?: boolean;
  isFreeStandingFacility?: boolean;
  isGreenDiamondProvider?: boolean;
  isBccProvider?: boolean;
  ddpCode?: string;
  isPremiumCarePhysician?: string;
  website?: string;
  address?: Address;
  virtualCareOffered?: string[];
  firstName?: string;
  middleName?: string;
  lastName?: string;
  surveyCount?: number;
  networkEndDate?: string;
  networkStartDate?: string;
  recommendationDetails?: RecommendationDetails;
  virtualIndicator?: string;
  uniqueIdentifier?: string;
}

const filterSearchResults = (searchResult) => {
  const providerSearchResult = get(searchResult, 'snackCardProviderSearch', {});
  const filteredResult = providerSearchResult.snackCardList;
  return {
    isTieredPlan: providerSearchResult.isTieredPlan,
    rulesPackageKey: providerSearchResult.rulesPackageKey,
    totalCount: providerSearchResult.totalCount,
    snackCardList: filteredResult,
    searchRadius: providerSearchResult?.searchRadius,
  };
};

export const useSnackCardProviderSearch = (options) => {
  const [filtredResult, setFiltredResultResult] = useState({});
  const [headers, setHeaders] = useState({});
  const [queryResult, queryAction] = useCustomQuery(
    GET_SNACK_CARD_PROVIDER_SEARCH_QUERY,
    {
      ...options,
      url: config('GRAPHQL_API_URL'),
      accessor: 'snackCardProviderSearch',
      onCompleted: async (result) => {
        const data = filterSearchResults(result?.data);
        if (options?.onCompleted) {
          options.onCompleted(data);
        }
        setFiltredResultResult(data);
        setHeaders({
          correlationId: result.headers[OPTUM_CORRELATION_ID_HEADER],
        });
      },
      onError: (error) => {
        if (options?.onError) {
          options.onError(error);
        }
      },
    }
  );

  const parsedResult = {
    ...queryResult,
    data: filtredResult,
    headers,
  };

  return [parsedResult, queryAction];
};

export const useSnackCardProviderResults = (variables) => {
  const featureFlags: [{ key: string; active: boolean }] =
    useLagoon('feature-flags')();

  const currentMember = getCurrentMember();
  const isEligibleForStandaloneDental = useIsEligibleForStandaloneDental();

  const [
    disablePCPIndAndANPEarlyPayFlag,
    disablePCPIndAndANPFlag,
    useMedicalNewRollUpCodes,
    useBHNewRollUpCodes,
    useVisionNewRollUpCodes,
    useDentalNewRollUpCodes,
    isFutureTerminationDemo,
    uspTierOneFlag,
    enableTier1FromMemberRPK,
    uspToggleFlag,
    enableMockProviderIdsForPreEffective,
    enableTierOneMockProviderIds,
    enableVirtualCareEpic,
  ] = useFeatureFlag([
    ConstantsLagoon.FEATURE_FLAGS.DISABLE_PCP_IND_AND_ANP_EARLYPAY,
    ConstantsLagoon.FEATURE_FLAGS.DISABLE_PCP_IND_AND_ANP,
    ConstantsLagoon.FEATURE_FLAGS.USE_MEDICAL_NEW_ROLLUP_CODES,
    ConstantsLagoon.FEATURE_FLAGS.USE_BH_NEW_ROLLUP_CODES,
    ConstantsLagoon.FEATURE_FLAGS.USE_VISION_NEW_ROLLUP_CODES,
    ConstantsLagoon.FEATURE_FLAGS.USE_DENTAL_NEW_ROLLUP_CODES,
    ConstantsLagoon.FEATURE_FLAGS.FUTURE_TERMINATION_DEMO,
    ConstantsLagoon.FEATURE_FLAGS.USP_TIER1_ENABLE,
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_TIER1_FROM_MEMBER_RPK,
    ConstantsLagoon.FEATURE_FLAGS.USP_ENABLE,
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_MOCK_PROVIDER_IDS_FOR_PRE_EFFECTIVE,
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_TIER_ONE_MOCK_PROVIDER_IDS,
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_VIRTUAL_CARE_EPIC,
  ]);

  const isVirtualCareUnetRailroadSuppressed = returnSuppressFlag(
    ConstantsLagoon.FEATURE_SUPPRESSION_FLAGS.VIRTUAL_CARE_UNET_RAILROAD
  );

  const disablePCPIndAndANP = currentMember.isIFPEarlyPay
    ? disablePCPIndAndANPEarlyPayFlag
    : disablePCPIndAndANPFlag;

  const { portalData } = useContext(PortalContext);

  const customPreferredPolicies: CustomPreferredPolicy[] = useLagoon(
    'custom-preferred-policies'
  )();
  const tierBenefits = useLagoon('tier-benefit')();
  const rollupCodeMapLagoonData = useLagoon('rollupcode-mapping')();

  const rollupCodeMap = rollupCodeMapLagoonData?.reduce((acc, item) => {
    if (item.source && item.destination) {
      acc[item.source] = item.destination;
    }
    return acc;
  }, {});

  const locale = getLanguage()?.code || 'en';
  const { storeCoverageType, storeLob } = useChipStore(
    useShallow((state: ChipState) => ({
      storeCoverageType: state.coverageType,
      storeLob: state.lob,
    }))
  );
  const {
    providerType,
    includeSpecialityRollupCodes,
    includeSpecRollupCodesArray,
    pcpIndicator,
    coverageType = storeCoverageType,
    onlineRetailers,
    shouldGetHGData = false,
    aoeCodes = [],
    behavioralProgramId,
    virtualCare = false,
    behavioralFacilityPrograms,
    search = '',
    isPCPLandingPage,
    executeImmediately = true,
  } = variables;

  const { longitude, latitude, stateCode } = useGeoLocationStorage();
  const allConfigs = useLagoon('config');
  const allConfigsArray = allConfigs();

  const searchRadius = getLagoonConfigValue(
    allConfigs,
    'DEFAULT_SEARCH_RADIUS'
  );
  const autoIncrementRadius = getLagoonConfigValue(
    allConfigs,
    'AUTO_INCREMENT_SEARCH_RADIUS'
  );
  const terminationProviderIdsDemo = getLagoonConfigValue(
    allConfigs,
    'TERMINATION_PROVIDER_IDS_DEMO'
  );
  const futureStartDateProviderIds = getLagoonConfigValue(
    allConfigs,
    'FUTURE_START_PROVIDERIDS'
  );
  const tierOneMockProviderIds = getLagoonConfigValue(
    allConfigs,
    'TIER1_MOCK_PROVIDERIDS'
  );
  const acoMockProviderIds = getLagoonConfigValue(
    allConfigs,
    'ACO_MOCK_PROVIDERIDS'
  );
  const premiumCareMockProviderIds = getLagoonConfigValue(
    allConfigs,
    'PREMIUM_MOCK_PROVIDERIDS'
  );
  const mnrFutureMockProviderIds = getLagoonConfigValue(
    allConfigs,
    'MNR_MOCK_PROVIDERIDS'
  );
  const minimumProvidersReturn = useMinProviderCount(providerType);
  const excludeNatAncForNetworkIds = getLagoonConfigValue(
    allConfigs,
    'EXCLUDE_NAT_ANC_FOR_NETWORK_IDS'
  );
  const autoIncrementRadiusArr = getAutoIncrementRadius(autoIncrementRadius);

  const medicalCoverage = getMemberHealthCoverageType(
    currentMember?.eligibility,
    CoverageTypesCodes.MEDICAL
  );
  const productCode = medicalCoverage?.productCode;
  const marketType = medicalCoverage?.marketType;

  const [snackCardProviderResults, getSnackCardProviderResults] =
    useSnackCardProviderSearch({
      onCompleted: variables.onCompleted,
      onError: variables.onError,
    });

  const {
    ddpCode,
    demographics,
    lineOfBusiness: memberLob,
    population,
    policyNumber,
  } = currentMember;
  const dateOfBirth = demographics?.dateOfBirth;
  const gender = demographics?.gender;

  const reciprocityId = getNetworkIdsForPES(
    currentMember,
    coverageType,
    featureFlags
  );

  const situsState = getSitusState(currentMember, memberLob);

  const isCustomPreferredPolicy: boolean = getIsCustomPreferredPolicy(
    customPreferredPolicies,
    policyNumber,
    reciprocityId,
    situsState
  );

  const memCatergory = currentMember?.membershipCategory || '';
  const membershipCategory = uspToggleFlag ? memCatergory : null;

  const isPreEffective = useCurrentMemberIsPreEffective();
  const currentMemberEffectiveDate = getCoverageEffDateRange(
    currentMember,
    coverageType
  ).startDate;
  const planVariationCode = getPlanVariationCode(currentMember, coverageType);
  const isGuestUser =
    portalData?.portalConfig?.isGuestUser ||
    storage.session.get(Constants.STORAGE_KEYS.SESSION.IS_GUEST);
  const planVariationCodeRequired = isPlanVariationCodeRequired(
    currentMember?.lineOfBusiness,
    currentMember?.population,
    portalData?.portalName,
    false,
    isGuestUser,
    isEligibleForStandaloneDental
  );
  const isPolicyNumberRequired = getIsPolicyIDRequired(
    currentMember,
    isGuestUser
  );

  const neccessaryParams = {
    ...(isPolicyNumberRequired ? { policyNumber } : {}),
    providerType,
    reciprocityId,
    latitude,
    longitude,
    coverageType,
    ...(planVariationCodeRequired ? { planVariationCode } : {}),
  };

  const isPractitioner =
    providerType?.toLowerCase() == ProviderTypes.PRACTITIONER;
  const isSmartChoice = useSmartChoice();
  const chipStore = useChipStore(useShallow((state: ChipState) => state));
  const selectedChip = chipStore?.chipValue;
  const isSmartChoiceEnabled =
    (isPractitioner ||
      (providerType?.toLowerCase() === ProviderTypes.ORGANIZATION &&
        selectedChip === CareCategories.FACILITIES)) &&
    isSmartChoice;
  const sortByValue = getSortByValue(isSmartChoiceEnabled);
  let recommendationPreferences = {};
  if (includeSpecialityRollupCodes) {
    recommendationPreferences = getRecommendationPayload(
      variables.includeSpecialityRollupCodes
    );
  } else {
    const memberRelevencyCodes = getMemberRelvancySpecialityRollupCodes({
      memberDemographics: {
        dateOfBirth: demographics?.dateOfBirth,
        gender: demographics?.gender,
      },
      providerType,
      lob: getLob(memberLob, storeLob),
      population,
      useNewAgeAndGenderRollups: true,
      pcpIndicator: demographics?.primaryCare?.providerID ? true : false,
    });
    recommendationPreferences = getRecommendationPayload(memberRelevencyCodes);
  }

  const memberCoverages = getCoverageTypes(currentMember);
  const memberPolicyNumber = currentMember?.policyNumber;
  const hasBH = memberHasBHCoverage(memberCoverages);
  const featureSuppressionData = useLagoon(
    Constants.LAGOON_TABLE.FEATURE_SUPPRESSION
  )();
  const virtualOnlyLocationIndFlagKey =
    ConstantsLagoon.FEATURE_SUPPRESSION_FLAGS
      .VIRTUAL_ONLY_LOCATION_INDICATOR_UNET_RAILROAD;
  const virtualOnlyLocationInd = shouldSuppressVirtualOnlyLocationIndicator(
    memberPolicyNumber,
    hasBH,
    virtualOnlyLocationIndFlagKey,
    featureSuppressionData
  );

  const regionalProviderArray = getRegionalVirtualCareProviderArray(
    selectedChip,
    allConfigsArray
  );

  const virtualCareLicenseStateCode = getVirtualCareLicenseStateCodes(
    regionalProviderArray
  );

  const enableRegionalVirtualCareSection =
    shouldEnableRegionalVirtualCareSection(
      enableVirtualCareEpic,
      isVirtualCareUnetRailroadSuppressed,
      regionalProviderArray
    );

  const params = {
    locale,
    longitude,
    latitude,
    stateCode,
    situsState: getSitusState(currentMember, getLob(memberLob, storeLob)),
    pcpIndicator: disablePCPIndAndANP && isPCPLandingPage ? null : pcpIndicator,
    isPCPLandingPage,
    useNewAgeAndGenderRollups: isGuestUser ? false : disablePCPIndAndANP,
    includeSpecialityRollupCodes: includeSpecialityRollupCodes ?? '',
    includeSpecRollupCodesArray: includeSpecRollupCodesArray ?? '',
    providerType: providerType ?? '',
    lob: getLob(memberLob, storeLob),
    population,
    membershipCategory,
    planYear: getMemberPlanYear(isPreEffective, currentMemberEffectiveDate),
    memberEffectiveDate: isPreEffective ? currentMemberEffectiveDate : null,
    planVariationCode,
    claimSystemTypeCode: getClaimOrEligibilitySystemTypeCode(
      currentMember,
      coverageType
    )?.claimSystemTypeCode,
    eligibilitySystemTypeCode: getClaimOrEligibilitySystemTypeCode(
      currentMember,
      coverageType
    )?.eligibilitySystemTypeCode,
    reciprocityId: getNetworkIdsForPES(
      currentMember,
      coverageType,
      featureFlags
    ),
    rollupCodeMap,
    rulesPackageKey: getRulesPackageKey(
      currentMember,
      tierBenefits,
      providerType,
      uspTierOneFlag,
      enableTier1FromMemberRPK
    ),
    snpType: conditionalValueRender(
      coverageType == ReverseCoverageTypesCodes.MEDICAL,
      medicalCoverage?.snpType,
      ''
    ),
    coverages: getCoverageTypes(currentMember),
    policyNumber,

    // remove distance limit for Virtual Care filter for BH providers ,
    // backend logic will handle -1 for no distance limit search,
    searchRadius:
      coverageType === 'B' && virtualCare === true
        ? -1
        : getSearchRadius(searchRadius),
    autoIncrementRadius: getAutoIncrementRadius(autoIncrementRadius),
    minimumProvidersReturn: minimumProvidersReturn || undefined,
    memberDemographics: {
      gender,
      dateOfBirth,
    },
    coverageType,
    onlineRetailers: getOnlineRetailers(onlineRetailers),
    useMedicalNewRollUpCodes,
    useBHNewRollUpCodes,
    useVisionNewRollUpCodes,
    useDentalNewRollUpCodes,
    isFutureTerminationDemo,
    terminationProviderIdsDemo,
    enableMockProviderIdsForPreEffective,
    enableTierOneMockProviderIds: enableTierOneMockProviderIds || false,
    futureStartDateProviderIds: getProviderIds(futureStartDateProviderIds),
    tierOneMockProviderIds: getProviderIds(tierOneMockProviderIds),
    acoMockProviderIds: getProviderIds(acoMockProviderIds),
    premiumCareMockProviderIds: getProviderIds(premiumCareMockProviderIds),
    mnrFutureMockProviderIds: getProviderIds(mnrFutureMockProviderIds),
    excludeNatAncForNetworkIds: getExcludeNatAncForNetworkIds(
      excludeNatAncForNetworkIds
    ),
    shouldGetHGData,
    ddpCode,
    aoeCodes,
    behavioralProgramId,
    virtualCare,
    isCustomPreferredPolicy,
    cesProductCode: productCode,
    memberMarketType: marketType,
    behavioralFacilityPrograms,
    invokeLlmModel: false,
    search,
    sortBy: sortByValue,
    ...(isSmartChoiceEnabled && {
      recommendationPreferences: {
        ...recommendationPreferences,
        recommendationRadius:
          coverageType === 'B' && virtualCare === true
            ? Number(
                autoIncrementRadiusArr?.[autoIncrementRadiusArr?.length - 1]
              )
            : getSearchRadius(searchRadius),
      },
      isProviderRecommendationsEnabled: isSmartChoiceEnabled,
    }),
    virtualOnlyLocationInd,
    isGuestUser,
    ...(enableRegionalVirtualCareSection && {
      virtualOnlyCare: regionalProviderArray,
      virtualCareLicenseStateCode,
    }),
    shouldBlockNationalProviders: shouldBlockNationalProviders(
      chipStore?.chipValue,
      enableVirtualCareEpic,
      isVirtualCareUnetRailroadSuppressed,
      allConfigsArray
    ),
  };

  const getSnackCardResults = useCallback(() => {
    if (isMissingParameters('useSnackCardProviderResults', neccessaryParams)) {
      return;
    }
    // If smart choice enabled and response is present from recommendation API
    if (
      isSmartChoice &&
      'uuid' in recommendationPreferences &&
      !('hasSetPreferences' in recommendationPreferences)
    ) {
      return;
    }

    getSnackCardProviderResults({
      variables: params,
    });
  }, [
    isSmartChoice,
    JSON.stringify(neccessaryParams),
    JSON.stringify(recommendationPreferences),
    JSON.stringify(params),
  ]);

  useEffect(() => {
    if (executeImmediately) {
      getSnackCardResults();
    }
  }, [
    // We need to trigger getSnackCardResults when any of these values change
    // hence we are adding them to the dependency array
    providerType,
    longitude,
    latitude,
    stateCode,
    pcpIndicator,
    gender,
    dateOfBirth,
    coverageType,
    onlineRetailers,
    search,
    JSON.stringify(recommendationPreferences),
    executeImmediately,
    getSnackCardResults,
  ]);

  return {
    snackCardProviderResults,
    getSnackCardResults,
    recommendationPreferences,
  };
};
